import * as React from 'react'
import { Button, Input, Card, CardBody, Nav, NavItem, NavLink } from 'reactstrap'
import { DropdownComponent, DropdownOption, OnChangeEvent } from 'components/dropdown/component'
import { RasaContext } from 'context'
import { SharedKeys, SharedStore } from 'shared/data-layer/sharedStore'
import { copyToClipboard } from 'generic/utility'
import { ChatResponse, PromptType, GenerationType } from 'shared_server_client/types/generative_ai'
import * as RexData from '../rex/data'
import * as RexConstants from '../rex/constants'
import * as Types from './types'
import * as Preferences from './preferences'
import './_styles.scss'

interface Props {
  title: string
  description: string
  url: string
  onApply: (summary: string) => void
  onCancel: () => void
  className?: string
  showFullSettings?: boolean
  showPromptMode?: boolean
}

interface State {
  isGenerating: boolean
  error: string
  generatedText: string
  versions: string[]
  versionDisplay: number
  versionGenerate: number
  showInputArea: boolean
  showCustomizeOptions: boolean
  customPrompt: string
  hasGenerated: boolean

  // Preferences
  selectedPreference: Types.UserPreference
  userPreferences: Types.UserPreference[]
  tone: DropdownOption
  length: DropdownOption
  language: DropdownOption
  emoji: DropdownOption
  format: DropdownOption
  complexity: DropdownOption
  perspective: DropdownOption
  creativity: DropdownOption
}

const emptyState: State = {
  isGenerating: false,
  error: '',
  generatedText: '',
  versions: [''],
  versionDisplay: 0,
  versionGenerate: 0,
  showInputArea: false, // Start in results view for auto-generation
  showCustomizeOptions: false, // Start with customize options hidden
  customPrompt: '',
  hasGenerated: false,
  selectedPreference: { key: '', value: '', description: 'Select Preference', options: null, is_default: false },
  userPreferences: [],
  tone: RexConstants.ToneDropdownOptions[0], // Neutral
  length: RexConstants.LengthDropdownOptions[1], // Medium
  language: RexConstants.LanguageDropdownOptions[0], // English
  emoji: RexConstants.EmojiDropdownOptions[0], // No Emojis
  format: RexConstants.FormatTypeOptions[0], // Text
  complexity: RexConstants.ComplexityDropdownOptions[1], // Intermediate
  perspective: RexConstants.PerspectiveDropdownOptions[0], // Neutral
  creativity: RexConstants.CreativityDropdownOptions[0], // Neutral
}

export class AISummaryComponent extends React.Component<Props, State> {
  public static contextType = RasaContext

  private communityId: string = ''
  private sharedStore: SharedStore

  constructor(props: Props) {
    super(props)
    this.state = {
      ...emptyState,
      showInputArea: props.showPromptMode || false,
    }
  }

  public componentDidMount() {
    this.sharedStore = SharedStore.instance(this.context)
    Promise.all([
      this.sharedStore.getValue(SharedKeys.activeCommunity),
    ]).then(([activeCommunity]) => {
      this.communityId = activeCommunity.communityId
      this.loadUserPreferences()
    })
  }

  private startAutoGeneration = () => {
    // Start generation immediately with default settings
    this.setState({
      showInputArea: false,
      isGenerating: true,
      error: '',
      generatedText: `✨ Crafting your perfect excerpt... Let ${RexConstants.AI_NAME} work its magic! 🚀`,
      versionGenerate: 0,
    }, () => {
      this.performGeneration()
    })
  }

  private loadUserPreferences = () => {
    Preferences.loadUserPreferences(this.communityId)
      .then((userPreferences) => {
        const defaultPreference = Preferences.getDefaultPreference(userPreferences)

        this.setState({
          userPreferences,
          selectedPreference: defaultPreference
        }, () => {
          // If we have a default preference, apply its settings
          if (defaultPreference && defaultPreference.options) {
            this.applyPreferenceSettings(defaultPreference.options)
          }
          // Start auto-generation after preferences are loaded (only if not in prompt mode)
          if (!this.props.showPromptMode) {
            this.startAutoGeneration()
          }
        })
      })
  }

  private applyPreferenceSettings = (options: any) => {
    const toneOption = RexConstants.ToneDropdownOptions.find(t => t.value === options.tone) || RexConstants.ToneDropdownOptions[0]
    const lengthOption = RexConstants.LengthDropdownOptions.find(l => l.value === options.length) || RexConstants.LengthDropdownOptions[1]
    const languageOption = RexConstants.LanguageDropdownOptions.find(l => l.value === options.language) || RexConstants.LanguageDropdownOptions[0]
    const emojiOption = RexConstants.EmojiDropdownOptions.find(e => e.value === options.emoji) || RexConstants.EmojiDropdownOptions[0]
    const formatOption = RexConstants.FormatTypeOptions.find(f => f.value === options.format) || RexConstants.FormatTypeOptions[0]
    const complexityOption = RexConstants.ComplexityDropdownOptions.find(c => c.value === options.complexity) || RexConstants.ComplexityDropdownOptions[1]
    const perspectiveOption = RexConstants.PerspectiveDropdownOptions.find(p => p.value === options.perspective) || RexConstants.PerspectiveDropdownOptions[0]
    const creativityOption = RexConstants.CreativityDropdownOptions.find(c => c.value === options.creativity) || RexConstants.CreativityDropdownOptions[0]

    this.setState({
      tone: toneOption,
      length: lengthOption,
      language: languageOption,
      emoji: emojiOption,
      format: formatOption,
      complexity: complexityOption,
      perspective: perspectiveOption,
      creativity: creativityOption,
    })
  }

  private cancelSummary = () => {
    this.props.onCancel()
  }

  private buildChatPayload(): any {
    const useCustomPreferences = this.state.selectedPreference.value === 'custom'

    return {
      // Required ChatPayload fields (from AnalyzePayload)

      // Required ChatPayload fields
      aiPromptType: PromptType.GENERATE_AI_SUMMARY,
      context: {
        content: this.props.description,
        title: this.props.title,
        url: this.props.url,
        prompt: this.props.showPromptMode ? this.state.customPrompt : '', // Use custom prompt if in prompt mode
        promptType: this.props.showPromptMode ? 'custom text generation' : 'article summary',
        emojis: useCustomPreferences ? this.state.emoji.value : (this.state.selectedPreference.options && this.state.selectedPreference.options.emoji || this.state.emoji.value),
        language: useCustomPreferences ? this.state.language.value : (this.state.selectedPreference.options && this.state.selectedPreference.options.language || this.state.language.value),
        length: useCustomPreferences ? this.state.length.value : (this.state.selectedPreference.options && this.state.selectedPreference.options.length || this.state.length.value),
        tone: useCustomPreferences ? this.state.tone.value : (this.state.selectedPreference.options && this.state.selectedPreference.options.tone || this.state.tone.value),
        format: useCustomPreferences ? this.state.format.value : (this.state.selectedPreference.options && this.state.selectedPreference.options.format || this.state.format.value),
        complexity: useCustomPreferences ? this.state.complexity.value : (this.state.selectedPreference.options && this.state.selectedPreference.options.complexity || this.state.complexity.value),
        perspective: useCustomPreferences ? this.state.perspective.value : (this.state.selectedPreference.options && this.state.selectedPreference.options.perspective || this.state.perspective.value),
        creativity: useCustomPreferences ? this.state.creativity.value : (this.state.selectedPreference.options && this.state.selectedPreference.options.creativity || this.state.creativity.value),
      },
      prompt: this.props.showPromptMode ? this.state.customPrompt : '', // Use custom prompt if in prompt mode
      promptType: this.props.showPromptMode ? 'custom text generation' : 'article summary',
      emojis: useCustomPreferences ? this.state.emoji.value : (this.state.selectedPreference.options && this.state.selectedPreference.options.emoji || this.state.emoji.value),
      language: useCustomPreferences ? this.state.language.value : (this.state.selectedPreference.options && this.state.selectedPreference.options.language || this.state.language.value),
      length: useCustomPreferences ? this.state.length.value : (this.state.selectedPreference.options && this.state.selectedPreference.options.length || this.state.length.value),
      tone: useCustomPreferences ? this.state.tone.value : (this.state.selectedPreference.options && this.state.selectedPreference.options.tone || this.state.tone.value),
      format: useCustomPreferences ? this.state.format.value : (this.state.selectedPreference.options && this.state.selectedPreference.options.format || this.state.format.value),
      complexity: useCustomPreferences ? this.state.complexity.value : (this.state.selectedPreference.options && this.state.selectedPreference.options.complexity || this.state.complexity.value),
      perspective: useCustomPreferences ? this.state.perspective.value : (this.state.selectedPreference.options && this.state.selectedPreference.options.perspective || this.state.perspective.value),
      creativity: useCustomPreferences ? this.state.creativity.value : (this.state.selectedPreference.options && this.state.selectedPreference.options.creativity || this.state.creativity.value),
      topics: [], // Empty array for topics
    }
  }

  private generateSummary = () => {
    if (this.props.showPromptMode && !this.state.customPrompt.trim()) {
      this.setState({ error: 'Please enter a prompt to generate content.' })
      return
    }
    if (!this.props.showPromptMode && !this.props.description.trim()) {
      this.setState({ error: 'Original description is required to generate excerpt.' })
      return
    }

    // If coming from input area and there are existing versions with content, create a new version
    const shouldCreateNewVersion = this.state.showInputArea &&
      this.state.versions.some(version => version.trim().length > 0)

    const generatingMessage = `✨ Crafting your perfect excerpt... Let ${RexConstants.AI_NAME} work its magic! 🚀`

    if (shouldCreateNewVersion) {
      this.setState({
        isGenerating: true,
        error: '',
        generatedText: generatingMessage,
        versions: this.state.versions.concat([generatingMessage]),
        versionDisplay: this.state.versions.length,
        versionGenerate: this.state.versions.length,
        hasGenerated: true,
      }, () => {
        this.performGeneration()
        this.setState({ showInputArea: false })
      })
    } else {
      this.setState({
        isGenerating: true,
        error: '',
        generatedText: generatingMessage,
        versionGenerate: this.state.versionDisplay,
        hasGenerated: true,
        versions: this.state.versions.map((message, index) => {
          if (index === this.state.versionDisplay) {
            return generatingMessage
          } else {
            return message
          }
        }),
      })
      this.performGeneration()
      this.setState({ showInputArea: false })
    }
  }

  private performGeneration = () => {
    const payload = this.buildChatPayload()

    try {
      RexData.streamRexResponse(
        this.communityId, 
        payload, 
        GenerationType.TEXT, 
        this.handleChatResponse
      )
    } catch (error) {
      this.setState({
        isGenerating: false,
        error: 'Failed to start excerpt generation. Please try again.',
        showInputArea: false
      })
    }
  }

  private handleChatResponse = (response: ChatResponse) => {
    // Handle null response (end of stream)
    if (!response) {
      return
    }

    if (response.error) {
      this.setState({
        isGenerating: false,
        error: response.error,
        showInputArea: false
      })
      return
    }

    if (response.message) {
      const currentText = this.state.generatedText
      const currentVersionText = this.state.versions[this.state.versionGenerate]
      const generatingMessage = `✨ Crafting your perfect excerpt... Let ${RexConstants.AI_NAME} work its magic! 🚀`
      
      // Check if current text is the generating message - if so, replace it and enable editing
      const isGeneratingMessage = currentText === generatingMessage || currentVersionText === generatingMessage
      
      this.setState({
        generatedText: isGeneratingMessage ? response.message : this.state.generatedText + response.message,
        versions: this.state.versions.map((message, index) => {
          if (index === this.state.versionGenerate) {
            return isGeneratingMessage ? response.message : message + response.message
          } else {
            return message
          }
        }),
        // Enable editing as soon as actual content starts streaming
        isGenerating: false
      })
    }

    if (response.done) {
      // isGenerating is already set to false when first message arrives
      const currentText = this.getCurrentText()
      if (!currentText || currentText.trim().length === 0) {
        this.setState({
          error: 'No excerpt was generated. Please try again or check your settings.'
        })
      }
    }
  }

  private updateGeneratedText = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      generatedText: event.target.value,
      versions: this.state.versions.map((message, index) => {
        if (index === this.state.versionDisplay) {
          return event.target.value
        } else {
          return message
        }
      }),
    })
  }

  private copyText = () => {
    copyToClipboard(this.state.generatedText)
  }

  private regenerateSummary = () => {
    const generatingMessage = `✨ Crafting your perfect excerpt... Let ${RexConstants.AI_NAME} work its magic! 🚀`
    this.setState({
      versions: this.state.versions.concat([generatingMessage]),
      versionDisplay: this.state.versions.length,
      versionGenerate: this.state.versions.length,
      generatedText: generatingMessage,
      showCustomizeOptions: false, // Hide customize options during regeneration
      hasGenerated: true,
    }, () => {
      this.generateSummary()
    })
  }

  private setVersion = (version: number) => {
    this.setState({
      versionDisplay: version,
      generatedText: this.state.versions[version],
    })
  }

  private getCurrentText = (): string => {
    return this.state.versions[this.state.versionDisplay] || this.state.generatedText
  }

  private hasCurrentVersion(): boolean {
    return !this.state.isGenerating && this.getCurrentText().length > 0
  }

  private applySummary = () => {
    const currentText = this.getCurrentText()
    if (currentText.trim()) {
      this.props.onApply(currentText)
    }
  }

  private setSelectedPreference = (event: OnChangeEvent) => {
    const selectedPref = this.state.userPreferences.find(p => p.value === event.selected.value)

    this.setState({
      selectedPreference: selectedPref || event.selected as Types.UserPreference,
      showCustomizeOptions: (selectedPref || event.selected).value === 'custom'
    }, () => {

      if (selectedPref && selectedPref.options) {
        this.applyPreferenceSettings(selectedPref.options)
      }
    })
  }

  private setSelectorValue = (key: string, event: OnChangeEvent) => {
    this.setState({
      ...this.state,
      [key]: event.selected,
    })
  }

  public render() {
    const { className = '' } = this.props

    return (
      <div className={`ai-summary-component ${className}`}>
        {this.state.showInputArea ? (
          // INPUT AREA
          <div className="ai-input-view">
            {/* AI Preference Section */}
            <div className="ai-preference-section">
              <div className="ai-preference-row">
                <label className="ai-label">Preference</label>
                <DropdownComponent
                  data={this.state.userPreferences}
                  selected={this.state.selectedPreference.key}
                  onChange={this.setSelectedPreference}
                  className="ai-preference-dropdown"
                />
              </div>
            </div>

            {/* Show customize options when custom is selected */}
            {this.state.selectedPreference.value === 'custom' && (
              <Card className="ai-customize-options-card">
                <CardBody>
                  <div className="ai-customize-grid">
                    <div className="ai-customize-option">
                      <label className="ai-option-label">Tone</label>
                      <DropdownComponent
                        data={RexConstants.ToneDropdownOptions}
                        selected={this.state.tone.key}
                        onChange={(e) => this.setSelectorValue('tone', e)}
                      />
                    </div>

                    <div className="ai-customize-option">
                      <label className="ai-option-label">Length</label>
                      <DropdownComponent
                        data={RexConstants.LengthDropdownOptions}
                        selected={this.state.length.key}
                        onChange={(e) => this.setSelectorValue('length', e)}
                      />
                    </div>

                    <div className="ai-customize-option">
                      <label className="ai-option-label">Language</label>
                      <DropdownComponent
                        data={RexConstants.LanguageDropdownOptions}
                        selected={this.state.language.key}
                        onChange={(e) => this.setSelectorValue('language', e)}
                      />
                    </div>

                    <div className="ai-customize-option">
                      <label className="ai-option-label">Include Emojis</label>
                      <DropdownComponent
                        data={RexConstants.EmojiDropdownOptions}
                        selected={this.state.emoji.key}
                        onChange={(e) => this.setSelectorValue('emoji', e)}
                      />
                    </div>

                    {/* Show additional options only when showFullSettings is true (default) */}
                    {this.props.showFullSettings !== false && (
                      <>
                        <div className="ai-customize-option">
                          <label className="ai-option-label">Format</label>
                          <DropdownComponent
                            data={RexConstants.FormatTypeOptions}
                            selected={this.state.format.key}
                            onChange={(e) => this.setSelectorValue('format', e)}
                          />
                        </div>

                        <div className="ai-customize-option">
                          <label className="ai-option-label">Complexity</label>
                          <DropdownComponent
                            data={RexConstants.ComplexityDropdownOptions}
                            selected={this.state.complexity.key}
                            onChange={(e) => this.setSelectorValue('complexity', e)}
                          />
                        </div>

                        <div className="ai-customize-option">
                          <label className="ai-option-label">Perspective</label>
                          <DropdownComponent
                            data={RexConstants.PerspectiveDropdownOptions}
                            selected={this.state.perspective.key}
                            onChange={(e) => this.setSelectorValue('perspective', e)}
                          />
                        </div>

                        <div className="ai-customize-option">
                          <label className="ai-option-label">Creativity</label>
                          <DropdownComponent
                            data={RexConstants.CreativityDropdownOptions}
                            selected={this.state.creativity.key}
                            onChange={(e) => this.setSelectorValue('creativity', e)}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </CardBody>
              </Card>
            )}

            <div className="ai-context-section">
              {this.props.showPromptMode ? (
                <div className="ai-context-item">
                  <label className="ai-label">Prompt</label>
                  <Input
                    type="textarea"
                    rows={2}
                    placeholder="Enter a prompt to generate content (e.g., 'Write engaging email content about...')"
                    value={this.state.customPrompt}
                    onChange={(e) => this.setState({ customPrompt: e.target.value })}
                    className="ai-prompt-input"
                  />
                </div>
              ) : (
                <>
                  <div className="ai-context-item">
                    <label className="ai-label">Article Title</label>
                    <div className="ai-context-text">{this.props.title}</div>
                  </div>
                  <div className="ai-context-item">
                    <label className="ai-label">Original Description</label>
                    <div className="ai-context-text ai-original-description">{this.props.description}</div>
                  </div>
                </>
              )}
            </div>

            <div className="ai-results-actions">
              {this.hasCurrentVersion() && (
                <Button
                  onClick={this.applySummary}
                  disabled={!this.hasCurrentVersion()}
                  className="ai-action-btn ai-apply-btn"
                >
                  <span>Use It!</span>
                </Button>
              )}

              <Button
                color="primary"
                onClick={this.state.hasGenerated ? this.regenerateSummary : this.generateSummary}
                disabled={this.state.isGenerating || (this.props.showPromptMode ? !this.state.customPrompt.trim() : !this.props.description.trim())}
                className="ai-action-btn"
              >
                <span>{this.state.isGenerating ? 'Generating...' : (this.state.hasGenerated ? 'Regenerate' : 'Generate')}</span>
              </Button>

              <Button
                color="outline-secondary"
                onClick={this.cancelSummary}
                className="ai-action-btn ai-cancel-btn"
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          // RESULTS AREA
          <div className="ai-results-view">
            <div className="ai-results-header">
              <div className="ai-preference-section">
                <div className="ai-preference-row">
                  <label className="ai-label">Preference</label>
                  <DropdownComponent
                    data={this.state.userPreferences}
                    selected={this.state.selectedPreference.key}
                    onChange={this.setSelectedPreference}
                    className="ai-preference-dropdown"
                  />
                </div>
              </div>

              {this.props.showPromptMode && (
                <div className="ai-context-section">
                  <div className="ai-context-item">
                    <label className="ai-label">Prompt</label>
                    <Input
                      type="textarea"
                      rows={2}
                      placeholder="Enter a prompt to generate content (e.g., 'Write engaging email content about...')"
                      value={this.state.customPrompt}
                      onChange={(e) => this.setState({ customPrompt: e.target.value })}
                      className="ai-prompt-input"
                      disabled={this.state.isGenerating}
                    />
                  </div>
                </div>
              )}

              {/* Show customize options when showCustomizeOptions is true and not generating */}
              {this.state.showCustomizeOptions && !this.state.isGenerating && (
                <Card className="ai-customize-options-card">
                  <CardBody>
                    <div className="ai-customize-grid">
                      <div className="ai-customize-option">
                        <label className="ai-option-label">Tone</label>
                        <DropdownComponent
                          data={RexConstants.ToneDropdownOptions}
                          selected={this.state.tone.key}
                          onChange={(e) => this.setSelectorValue('tone', e)}
                        />
                      </div>

                      <div className="ai-customize-option">
                        <label className="ai-option-label">Length</label>
                        <DropdownComponent
                          data={RexConstants.LengthDropdownOptions}
                          selected={this.state.length.key}
                          onChange={(e) => this.setSelectorValue('length', e)}
                        />
                      </div>

                      <div className="ai-customize-option">
                        <label className="ai-option-label">Language</label>
                        <DropdownComponent
                          data={RexConstants.LanguageDropdownOptions}
                          selected={this.state.language.key}
                          onChange={(e) => this.setSelectorValue('language', e)}
                        />
                      </div>

                      <div className="ai-customize-option">
                        <label className="ai-option-label">Include Emojis</label>
                        <DropdownComponent
                          data={RexConstants.EmojiDropdownOptions}
                          selected={this.state.emoji.key}
                          onChange={(e) => this.setSelectorValue('emoji', e)}
                        />
                      </div>

                      {/* Show additional options only when showFullSettings is true (default) */}
                      {this.props.showFullSettings !== false && (
                        <>
                          <div className="ai-customize-option">
                            <label className="ai-option-label">Format</label>
                            <DropdownComponent
                              data={RexConstants.FormatTypeOptions}
                              selected={this.state.format.key}
                              onChange={(e) => this.setSelectorValue('format', e)}
                            />
                          </div>

                          <div className="ai-customize-option">
                            <label className="ai-option-label">Complexity</label>
                            <DropdownComponent
                              data={RexConstants.ComplexityDropdownOptions}
                              selected={this.state.complexity.key}
                              onChange={(e) => this.setSelectorValue('complexity', e)}
                            />
                          </div>

                          <div className="ai-customize-option">
                            <label className="ai-option-label">Perspective</label>
                            <DropdownComponent
                              data={RexConstants.PerspectiveDropdownOptions}
                              selected={this.state.perspective.key}
                              onChange={(e) => this.setSelectorValue('perspective', e)}
                            />
                          </div>

                          <div className="ai-customize-option">
                            <label className="ai-option-label">Creativity</label>
                            <DropdownComponent
                              data={RexConstants.CreativityDropdownOptions}
                              selected={this.state.creativity.key}
                              onChange={(e) => this.setSelectorValue('creativity', e)}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </CardBody>
                </Card>
              )}
            </div>

            <div className="ai-results-content">
              {/* Version tabs */}
              {this.state.versions.length > 1 && (
                <Nav tabs className="ai-version-tabs">
                  {this.state.versions.map((message, index) => (
                    <NavItem key={index}>
                      <NavLink
                        className={this.state.versionDisplay === index ? 'active' : ''}
                        onClick={() => this.setVersion(index)}
                      >
                        <span>
                          {(this.state.isGenerating && this.state.versionGenerate === index) ? '* ' : ''}Version {index + 1}
                        </span>
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
              )}

              <div className="ai-results-main">
                {(this.state.generatedText || this.getCurrentText()) && (
                  <Input
                    type="textarea"
                    value={this.getCurrentText()}
                    onChange={this.updateGeneratedText}
                    className="ai-result-text-editable"
                    disabled={this.state.isGenerating}
                  />
                )}

                {this.state.error && (
                  <div className="ai-error">
                    <div className="alert alert-danger" role="alert">
                      {this.state.error}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {(this.state.showInputArea || !this.state.isGenerating && (this.state.error || this.hasCurrentVersion() || this.state.versions.length > 1 || this.props.showPromptMode)) && (
              <div className="ai-results-actions">
                {this.hasCurrentVersion() && (
                  <Button
                    onClick={this.applySummary}
                    disabled={!this.hasCurrentVersion()}
                    className="ai-action-btn ai-apply-btn"
                  >
                    <span>Use It!</span>
                  </Button>
                )}

                <Button
                  color="primary"
                  onClick={this.state.hasGenerated ? this.regenerateSummary : this.generateSummary}
                  disabled={this.state.isGenerating || (this.props.showPromptMode ? !this.state.customPrompt.trim() : !this.props.description.trim())}
                  className="ai-action-btn"
                >
                  <span>{this.state.isGenerating ? 'Generating...' : (this.state.hasGenerated ? 'Regenerate' : 'Generate')}</span>
                </Button>

                {/* Copy button hidden but functionality preserved */}
                <Button
                  onClick={this.copyText}
                  disabled={!this.hasCurrentVersion()}
                  className="ai-action-btn"
                  style={{ display: 'none' }}
                >
                  <span>Copy</span>
                </Button>


                <Button
                  color="outline-secondary"
                  onClick={this.cancelSummary}
                  className="ai-action-btn ai-cancel-btn"
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }
}